import { PlusFillIcon, TrashIcon } from "../../shared/Icons";
import { themeAtom } from "../../shared/atoms";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { generateUUID } from "../../shared/services/utils";
import {
  Bq8MooringM,
  MooringBollardsDetailsM,
  MooringFairleadsDetailsM,
  MooringRopeDetailsM,
  MooringWinchDetailsM,
  initialBq8Mooring,
  mapSqlToBq8MooringForm,
} from "../models/bq8-mooring.model";
import { bqF8MooringAtom } from "../state/baltic-form.atoms";
import { formDate } from "../services/baltic-forms.utils";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

interface SectionEightProps {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq8Mooring: React.FC<SectionEightProps> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF8MooringAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq8MooringM>(initialBq8Mooring);

  const handleMooringRopeChange = (i: number, field: keyof MooringRopeDetailsM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.mooringRopes];
      updated[i] = { ...updated[i], [field]: value };
      return { ...prev, mooringRopes: updated };
    });
  };

  const handleMooringWinchChange = (i: number, field: keyof MooringWinchDetailsM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.mooringWinches];
      updated[i] = { ...updated[i], [field]: value };
      return { ...prev, mooringWinches: updated };
    });
  };

  const handleMooringBollardChange = (i: number, field: keyof MooringBollardsDetailsM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.mooringBollards];
      updated[i] = { ...updated[i], [field]: value };
      return { ...prev, mooringBollards: updated };
    });
  };

  const handleMooringFairleadChange = (i: number, field: keyof MooringFairleadsDetailsM, value: string) => {
    setForm((prev) => {
      const updated = [...prev.mooringFairleads];
      updated[i] = { ...updated[i], [field]: value };
      return { ...prev, mooringFairleads: updated };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      let nextForm = form;

      if (nextForm.mooringRopes.length === 0) {
        nextForm = {
          ...nextForm,
          mooringRopes: [
            {
              ...initialBq8Mooring.mooringRopes[0],
              ropeId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm.mooringWinches.length === 0) {
        nextForm = {
          ...nextForm,
          mooringWinches: [
            {
              ...initialBq8Mooring.mooringWinches[0],
              winchId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm.mooringFairleads.length === 0) {
        nextForm = {
          ...nextForm,
          mooringFairleads: [
            {
              ...initialBq8Mooring.mooringFairleads[0],
              fairleadId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm.mooringBollards.length === 0) {
        nextForm = {
          ...nextForm,
          mooringBollards: [
            {
              ...initialBq8Mooring.mooringBollards[0],
              bollardId: generateUUID(),
            },
          ],
        };
      }

      if (nextForm !== form) {
        setForm(nextForm);
        return;
      }

      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    setIsLoading(true);

    let bollardsData = [];
    let fairleadsData = [];
    let ropesData = [];
    let winchesData = [];

    try {
      // Bollards
      try {
        const res = await api.get(`${apiUrl}/bq_MooringBollards`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        bollardsData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Mooring Bollards not found (404)");
        } else {
          console.error("Error fetching Mooring Bollards", error);
        }
      }

      // Fairleads
      try {
        const res = await api.get(`${apiUrl}/bq_MooringFairleads`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        fairleadsData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Mooring Fairleads not found (404)");
        } else {
          console.error("Error fetching Mooring Fairleads", error);
        }
      }

      // Ropes
      try {
        const res = await api.get(`${apiUrl}/bq_MooringRopes`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        ropesData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Mooring Ropes not found (404)");
        } else {
          console.error("Error fetching Mooring Ropes", error);
        }
      }

      // Winches
      try {
        const res = await api.get(`${apiUrl}/bq_MooringWinches`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        winchesData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Mooring Winches not found (404)");
        } else {
          console.error("Error fetching Mooring Winches", error);
        }
      }

      const _form = mapSqlToBq8MooringForm(ropesData, fairleadsData, bollardsData, winchesData);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error) {
      console.error("Unexpected error fetching mooring data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const addRope = () => {
    let _mooringRopes = [...form.mooringRopes];
    _mooringRopes.push({
      ...initialBq8Mooring.mooringRopes[0],
      ropeId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, mooringRopes: _mooringRopes }));
  };

  const removeRope = (i: number) => {
    const id = form.mooringRopes[i].ropeId;
    deleteMooringRope(id);
    let _mooringRopes = [...form.mooringRopes];
    _mooringRopes.splice(i, 1);
    setForm((prev) => ({ ...prev, mooringRopes: _mooringRopes }));
  };

  const deleteMooringRope = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelMooringRope`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Mooring rope not found (404)");
      } else {
        console.error("Error deleting mooring rope", error);
      }
    }
  };

  const addWinch = () => {
    let _mooringWinches = [...form.mooringWinches];
    _mooringWinches.push({
      ...initialBq8Mooring.mooringWinches[0],
      winchId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, mooringWinches: _mooringWinches }));
  };

  const removeWinch = (i: number) => {
    const id = form.mooringWinches[i].winchId;
    deleteMooringWinche(id);
    let _mooringWinches = [...form.mooringWinches];
    _mooringWinches.splice(i, 1);
    setForm((prev) => ({ ...prev, mooringWinches: _mooringWinches }));
  };

  const deleteMooringWinche = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelMooringWinch`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Mooring winch not found (404)");
      } else {
        console.error("Error deleting mooring winch", error);
      }
    }
  };

  const addBollard = () => {
    let _mooringBollards = [...form.mooringBollards];
    _mooringBollards.push({
      ...initialBq8Mooring.mooringBollards[0],
      bollardId: generateUUID(),
    });

    setForm((prev) => ({ ...prev, mooringBollards: _mooringBollards }));
  };

  const removeBollard = (i: number) => {
    const id = form.mooringBollards[i].bollardId;
    deleteMooringBollard(id);
    let _mooringBollards = [...form.mooringBollards];
    _mooringBollards.splice(i, 1);
    setForm((prev) => ({ ...prev, mooringBollards: _mooringBollards }));
  };

  const deleteMooringBollard = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelMooringBollard`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Mooring bollard not found (404)");
      } else {
        console.error("Error deleting mooring bollard", error);
      }
    }
  };

  const addFairlead = () => {
    let _mooringFairleads = [...form.mooringFairleads];
    _mooringFairleads.push({
      ...initialBq8Mooring.mooringFairleads[0],
      fairleadId: generateUUID(),
    });
    setForm((prev) => ({ ...prev, mooringFairleads: _mooringFairleads }));
  };

  const removeFairlead = (i: number) => {
    const id = form.mooringFairleads[i].fairleadId;
    deleteMooringFairlead(id);
    let _mooringFairleads = [...form.mooringFairleads];
    _mooringFairleads.splice(i, 1);
    setForm((prev) => ({ ...prev, mooringFairleads: _mooringFairleads }));
  };

  const deleteMooringFairlead = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelMooringFairlead`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Mooring fairlead not found (404)");
      } else {
        console.error("Error deleting mooring fairlead", error);
      }
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorSize="lg" loadingIndicatorClassName="text-green">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className="custom-heading mb-4">Mooring</h1>
        <form onSubmit={handleSubmit}>
          {/* Mooring Ropes table */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">
            Provide details for Mooring Ropes, Wires, Tails and Shackles:
          </h2>
          <div
            className={twMerge(
              "overflow-x-auto",
              "[&::-webkit-scrollbar]:h-1.5",
              "[&::-webkit-scrollbar-track]:bg-transparent",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              theme.uwhcAssistant.tableScroll,
              theme.uwhcAssistant.scrolHover,
            )}
          >
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="min-w-[10px] max-w-[30px] px-1 py-1">#</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Type</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Location / Identity</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Material</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Diameter/size (mm)</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Length</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">LDBF(100-105% SDMBL, Tonnes)</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">TDBF(125-130% SDMBL, Tonnes)</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">SWL (tonnes)</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">WLL (50-55% of Max LDBF)</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Certificate No.</th>
                  <th className="min-w-[155px] max-w-[195px] px-1 py-1">Installed Date</th>
                  <th className="min-w-[155px] max-w-[195px] px-1 py-1">Reversed Date</th>
                  <th className="min-w-[155px] max-w-[195px] px-1 py-1">Renewal2 Date</th>
                  <th className="min-w-[90px] max-w-[130px] px-1 py-1">Status of line/tail</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Condition of line/tail</th>
                  <th className="min-w-[10px] max-w-[30px] px-1 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {form.mooringRopes?.map((mr, i) => (
                  <tr key={mr.id || i} className={twMerge("border-t", theme.uwhcAssistant.border)}>
                    <td className="min-w-[10px] max-w-[30px] px-1 py-1">{i + 1}</td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <select
                        disabled={readonly}
                        value={mr.type}
                        onChange={(e) => handleMooringRopeChange(i, "type", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Mooring Wires">Mooring Wires</option>
                        <option value="Ropes">Ropes</option>
                        <option value="Tails">Tails</option>
                        <option value="Shackles">Shackles</option>
                      </select>
                    </td>
                    <td className="min-w-[100px] max-w-[140px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="text"
                        value={mr.locationIdentity}
                        onChange={(e) => handleMooringRopeChange(i, "locationIdentity", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[100px] max-w-[140px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="text"
                        value={mr.material}
                        onChange={(e) => handleMooringRopeChange(i, "material", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[100px] max-w-[140px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.diameterSize}
                        onChange={(e) => handleMooringRopeChange(i, "diameterSize", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[100px] max-w-[140px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.length}
                        onChange={(e) => handleMooringRopeChange(i, "length", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.ldbfTonnes}
                        onChange={(e) => handleMooringRopeChange(i, "ldbfTonnes", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.tdbfTonnes}
                        onChange={(e) => handleMooringRopeChange(i, "tdbfTonnes", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.swlTonnes}
                        onChange={(e) => handleMooringRopeChange(i, "swlTonnes", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mr.wllTonnes}
                        onChange={(e) => handleMooringRopeChange(i, "wllTonnes", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="text"
                        value={mr.certificateNo}
                        onChange={(e) => handleMooringRopeChange(i, "certificateNo", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    <td className="min-w-[155px] max-w-[195px] px-1 py-1">
                      <Datepicker
                        disabled={readonly}
                        selected={formDate(mr.installedDate ?? "")}
                        onChange={(date) => handleMooringRopeChange(i, "installedDate", date.toISOString())}
                      />
                    </td>
                    <td className="min-w-[155px] max-w-[195px] px-1 py-1">
                      <Datepicker
                        disabled={readonly}
                        selected={formDate(mr.reversedDate ?? "")}
                        onChange={(date) => handleMooringRopeChange(i, "reversedDate", date.toISOString())}
                      />
                    </td>
                    <td className="min-w-[155px] max-w-[195px] px-1 py-1">
                      <Datepicker
                        disabled={readonly}
                        selected={formDate(mr.renewalDate ?? "")}
                        onChange={(date) => handleMooringRopeChange(i, "renewalDate", date.toISOString())}
                      />
                    </td>
                    <td className="min-w-[90px] max-w-[130px] px-1 py-1">
                      <select
                        disabled={readonly}
                        value={mr.statusLineTail}
                        onChange={(e) => handleMooringRopeChange(i, "statusLineTail", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="In Use">In Use</option>
                        <option value="Spare">Spare</option>
                      </select>
                    </td>
                    <td className="min-w-[100px] max-w-[140px] px-1 py-1">
                      <select
                        disabled={readonly}
                        value={mr.conditionLineTail}
                        onChange={(e) => handleMooringRopeChange(i, "conditionLineTail", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Suitable">Suitable</option>
                        <option value="To be renewed">To be renewed</option>
                      </select>
                    </td>
                    {!readonly && (
                      <td className="min-w-[10px] max-w-[30px] px-1 py-1">
                        <button className="text-red-500" onClick={() => removeRope(i)}>
                          <TrashIcon height={22} width={22} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!readonly && (
            <button className="mb-8 mt-4 inline-flex items-center rounded py-2 text-green" onClick={addRope}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Rope</span>
            </button>
          )}

          {/* Details of winches and brake testing */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">
            Details of winches and brake testing including rendering loads:
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="min-w-[10px] max-w-[30px] px-1 py-1">#</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Mooring winch location</th>
                  <th className="min-w-[80px] max-w-[120px] px-1 py-1">Split drum</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Motive power</th>
                  <th className="min-w-[80px] max-w-[120px] px-1 py-1">Remote operational controls</th>
                  <th className="min-w-[100px] max-w-[140px] px-1 py-1">Heaving power</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Hauling speed</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Type of brake</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">
                    Designed Brake Max holding load (ISO) (80% of SDMBL)
                  </th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">
                    Operational brake holding load (60% of SDMBL)
                  </th>
                  <th className="min-w-[155px] max-w-[195px] px-1 py-1">Date of last brake test</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Brake rendering load</th>
                  <th className="min-w-[120px] max-w-[160px] px-1 py-1">Frequency of testing brakes</th>
                  <th className="min-w-[10px] max-w-[30px] px-1 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {form.mooringWinches?.map((mw, i) => (
                  <tr key={mw.id} className={twMerge("border-t", theme.uwhcAssistant.border)}>
                    <td className="min-w-[10px] max-w-[30px] px-1 py-1">{i + 1}</td>
                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.mooringWinchLocation}
                        onChange={(e) => handleMooringWinchChange(i, "mooringWinchLocation", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[80px] max-w-[120px] px-1 py-1">
                      <select
                        disabled={readonly}
                        name="splitDrum"
                        value={mw.splitDrum}
                        onChange={(e) => handleMooringWinchChange(i, "splitDrum", e.target.value)}
                        className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <select
                        disabled={readonly}
                        name="motivePower"
                        value={mw.motivePower}
                        onChange={(e) => handleMooringWinchChange(i, "motivePower", e.target.value)}
                        className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Steam">Steam</option>
                        <option value="Electric">Electric</option>
                        <option value="Hydraulic">Hydraulic</option>
                        <option value="Other">Other</option>
                      </select>
                    </td>

                    <td className="min-w-[60px] max-w-[100px] px-1 py-1">
                      <select
                        disabled={readonly}
                        name="remoteControls"
                        value={mw.remoteControls}
                        onChange={(e) => handleMooringWinchChange(i, "remoteControls", e.target.value)}
                        className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.heavingPower}
                        onChange={(e) => handleMooringWinchChange(i, "heavingPower", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.haulingSpeed}
                        onChange={(e) => handleMooringWinchChange(i, "haulingSpeed", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <select
                        disabled={readonly}
                        name="typeOfBrake"
                        value={mw.typeOfBrake}
                        onChange={(e) => handleMooringWinchChange(i, "typeOfBrake", e.target.value)}
                        className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
                      >
                        <option value=""></option>
                        <option value="Manual">Manual</option>
                        <option value="Spring">Spring</option>
                        <option value="Hydraulic">Hydraulic</option>
                        <option value="Other(specify)">Other(specify)</option>
                      </select>
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.designedBrakeLoad}
                        onChange={(e) => handleMooringWinchChange(i, "designedBrakeLoad", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.operationalBrakeLoad}
                        onChange={(e) => handleMooringWinchChange(i, "operationalBrakeLoad", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[155px] max-w-[195px] px-1 py-1">
                      <Datepicker
                        disabled={readonly}
                        selected={formDate(mw.dateLastBrakeTest ?? "")}
                        onChange={(date) => handleMooringWinchChange(i, "dateLastBrakeTest", date.toISOString())}
                      />
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="number"
                        value={mw.brakeRenderingLoad}
                        onChange={(e) => handleMooringWinchChange(i, "brakeRenderingLoad", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>

                    <td className="min-w-[120px] max-w-[160px] px-1 py-1">
                      <input
                        disabled={readonly}
                        type="text"
                        value={mw.brakeTestingFrequency}
                        onChange={(e) => handleMooringWinchChange(i, "brakeTestingFrequency", e.target.value)}
                        className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                      />
                    </td>
                    {!readonly && (
                      <td className="min-w-[10px] max-w-[30px] px-1 py-1">
                        <button className="mt-2 text-xs text-red-600" onClick={() => removeWinch(i)}>
                          <TrashIcon width={22} height={22} />
                        </button>
                      </td>
                    )}
                    {/* Remove */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!readonly && (
            <button className="mb-8 mt-4 inline-flex items-center rounded py-2 text-green" onClick={addWinch}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Winch</span>
            </button>
          )}

          {/* Mooring bollards/bitts */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Provide details of Mooring bollards/bitts:</h2>
          <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.border)}>
            <div className="w-[2%]">
              <label>#</label>
            </div>
            <div className="w-[10%]">
              <label>Location</label>
            </div>
            <div className="w-[10%]">
              <label>Identity No</label>
            </div>
            <div className="w-[10%]">
              <label>Certificate Number</label>
            </div>
            <div className="w-[12%]">
              <label>Size (mm)</label>
            </div>
            <div className="w-[10%]">
              <label>SWL (Tonnes)</label>
            </div>
            <div className="w-[2%] text-center"></div>
          </div>
          {form.mooringBollards?.map((mb, i) => (
            <div key={mb.id} className="mb-4 flex items-center gap-2">
              <div className="w-[2%]">{i + 1}</div>
              <div className="w-[10%]">
                <select
                  disabled={readonly}
                  name="location"
                  value={mb.location}
                  onChange={(e) => handleMooringBollardChange(i, "location", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  <option value="Forecastle">Forecastle</option>
                  <option value="Maindeck Forward (Port)">Maindeck Forward (Port)</option>
                  <option value="Maindeck Forward (Stbd)">Maindeck Forward (Stbd)</option>
                  <option value="Poop Deck (Port)">Poop Deck (Port)</option>
                  <option value="Poop Deck (Stbd)">Poop Deck (Stbd)</option>
                </select>
              </div>

              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mb.identityNo}
                  onChange={(e) => handleMooringBollardChange(i, "identityNo", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mb.certificateNo}
                  onChange={(e) => handleMooringBollardChange(i, "certificateNo", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mb.sizeMm}
                  onChange={(e) => handleMooringBollardChange(i, "sizeMm", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>

              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mb.swl}
                  onChange={(e) => handleMooringBollardChange(i, "swl", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>
              {!readonly && (
                <div className="w-[10%]">
                  <button className="mt-2 text-xs text-red-600" onClick={() => removeBollard(i)}>
                    <TrashIcon width={22} height={22} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {!readonly && (
            <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addBollard}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Bollard</span>
            </button>
          )}

          {/* Mooring fairleads/chocks */}
          <h2 className="mb-2 mt-[120px] text-lg font-semibold">Provide details of Mooring Fairleads/Chocks</h2>
          <div className={twMerge("mb-4 flex gap-2 border-b pb-2 text-xs font-bold", theme.uwhcAssistant.border)}>
            <div className="w-[2%]">
              <label>#</label>
            </div>
            <div className="w-[10%]">
              <label>Type</label>
            </div>
            <div className="w-[10%]">
              <label>Location</label>
            </div>
            <div className="w-[10%]">
              <label>Identity No</label>
            </div>
            <div className="w-[12%]">
              <label>Certificate Number</label>
            </div>
            <div className="w-[10%]">
              <label>Size (mm)</label>
            </div>
            <div className="w-[10%]">
              <label>SWL (Tonnes)</label>
            </div>
            <div className="w-[10%]">
              <label>Modifications</label>
            </div>
            <div className="w-[23%]">
              <label>If yes, are modifications class approved?</label>
            </div>
            <div className="w-[2%] text-center"></div>
          </div>
          {form.mooringFairleads?.map((mf, i) => (
            <div key={mf.id} className="mb-4 flex items-center gap-2">
              <div className="w-[2%]">{i + 1}</div>
              <div className="w-[10%]">
                <select
                  disabled={readonly}
                  name="type"
                  value={mf.type}
                  onChange={(e) => handleMooringFairleadChange(i, "type", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  <option value="Open roller type">Open roller type</option>
                  <option value="Panama type">Panama type</option>
                  <option value="Closed roller type">Closed roller type</option>
                  <option value="Universal roller fairlead">Universal roller fairlead</option>
                  <option value="Closed chock">Closed chock</option>
                  <option value="Roller fairlead with stopping/jumping bar">
                    Roller fairlead with stopping/jumping bar
                  </option>
                  <option value="Roller fairlead protected by bulwark">Roller fairlead protected by bulwark</option>
                </select>
              </div>
              <div className="w-[10%]">
                <select
                  disabled={readonly}
                  name="location"
                  value={mf.location}
                  onChange={(e) => handleMooringFairleadChange(i, "location", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  <option value="Forecastle">Forecastle</option>
                  <option value="Maindeck Forward (Port)">Maindeck Forward (Port)</option>
                  <option value="Maindeck Forward (Stbd)">Maindeck Forward (Stbd)</option>
                  <option value="Poop Deck (Port)">Poop Deck (Port)</option>
                  <option value="Poop Deck (Stbd)">Poop Deck (Stbd)</option>
                </select>
              </div>
              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mf.identityNo}
                  onChange={(e) => handleMooringFairleadChange(i, "identityNo", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>
              <div className="w-[12%]">
                <input
                  disabled={readonly}
                  type="text"
                  value={mf.certificateNo}
                  onChange={(e) => handleMooringFairleadChange(i, "certificateNo", e.target.value)}
                  className="w-full rounded-md border p-2 text-sm"
                />
              </div>
              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mf.sizeMm}
                  onChange={(e) => handleMooringFairleadChange(i, "sizeMm", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>
              <div className="w-[10%]">
                <input
                  disabled={readonly}
                  type="number"
                  value={mf.swl}
                  onChange={(e) => handleMooringFairleadChange(i, "swl", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                />
              </div>
              <div className="w-[10%]">
                <select
                  disabled={readonly}
                  name="modifications"
                  value={mf.modifications}
                  onChange={(e) => handleMooringFairleadChange(i, "modifications", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className="w-[23%]">
                <select
                  disabled={readonly}
                  name="modificationsClassApproved"
                  value={mf.modificationsClassApproved}
                  onChange={(e) => handleMooringFairleadChange(i, "modificationsClassApproved", e.target.value)}
                  className="w-full rounded-md border p-2 focus:outline-none focus:ring"
                >
                  <option value=""></option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              {!readonly && (
                <div className="w-[2%]">
                  <button className="mt-2 text-xs text-red-600" onClick={() => removeFairlead(i)}>
                    <TrashIcon width={22} height={22} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {!readonly && (
            <button className="mb-8 inline-flex items-center rounded py-2 text-green" onClick={addFairlead}>
              <PlusFillIcon className="inline-block h-6 w-6 pr-1" />
              <span className="text-sm hover:underline focus:outline-none">Add New Fairlead</span>
            </button>
          )}

          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
